import { useParams, Navigate, Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";
import { MATERIAUX_DETAIL } from "@/lib/materiauxDetail";
import { partnersForMaterial, TRADE_BY_MATERIAL } from "@/lib/partenaires";
import { useSEO, BASE } from "@/lib/seo";
import ArticleShell, { H2, P, SeeAlso } from "@/components/site/ArticleShell";
import { RichText } from "@/components/site/RichText";
import { IlluFigure } from "@/components/site/IlluFigure";
import { MATERIAU_STEP_ILLUS } from "@/lib/illustrations";

const OTHERS = (slug) =>
  Object.entries(MATERIAUX_DETAIL)
    .filter(([key]) => key !== slug)
    .slice(0, 3)
    .map(([key, m]) => ({ to: `/materiaux-et-techniques/${key}`, label: m.name }));

// Encadré « Artisans partenaires » en bas de chaque fiche matériau :
// partenaires du métier lié s'ils existent, sinon appel à candidature.
const PartnersSection = ({ slug }) => {
  const entries = partnersForMaterial(slug);
  return (
    <section className="mt-16 border border-ink/15 p-8 sm:p-10" data-testid="materiau-partenaires">
      <p className="eyebrow">Artisans partenaires</p>
      {entries.length > 0 ? (
        <>
          <h2 className="mt-4 font-serif text-2xl font-normal leading-snug text-ink sm:text-3xl">
            Nos artisans partenaires en {entries[0].metierName.toLowerCase()}
          </h2>
          <div className="mt-8 space-y-10">
            {entries.map((p) => (
              <div key={p.id} data-testid={`materiau-partner-${p.id}`}>
                <p className="font-serif text-xl text-ink">{p.name}</p>
                <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.16em] text-bronze">
                  {p.city}
                </p>
                <p className="mt-3 text-base font-light leading-relaxed text-body">{p.blurb}</p>
                <Link
                  to={`/partenaires/${p.metierSlug}`}
                  data-testid={`materiau-partner-link-${p.id}`}
                  className="group mt-3 inline-flex items-center gap-3 font-mono text-[12px] uppercase tracking-[0.18em] text-bronze"
                >
                  <span className="link-underline">Découvrir sa fiche</span>
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </div>
            ))}
          </div>
          <Link
            to="/partenaires-metiers-d-art"
            data-testid="materiau-partenaires-hub-link"
            className="group mt-10 inline-flex items-center gap-3 font-mono text-[12px] uppercase tracking-[0.18em] text-ink transition-colors duration-300 hover:text-bronze"
          >
            <span className="link-underline">Voir tous nos partenaires métiers d'art</span>
            <ArrowRight className="h-4 w-4 text-bronze transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </>
      ) : (
        <>
          <h2 className="mt-4 font-serif text-2xl font-normal leading-snug text-ink sm:text-3xl">
            Vous êtes {TRADE_BY_MATERIAL[slug] || "artisan des métiers d'art"} ?
          </h2>
          <p className="mt-4 text-base font-light leading-relaxed text-body">
            Rejoignez notre réseau d'artisans partenaires et faites découvrir votre savoir-faire.
          </p>
          <Link
            to="/devenez-partenaire-metiers-d-art"
            data-testid="materiau-devenir-btn"
            className="group mt-6 inline-flex items-center gap-3 bg-ink px-7 py-4 font-mono text-[12px] uppercase tracking-[0.18em] text-paper transition-colors duration-300 hover:bg-bronze"
          >
            Inscription gratuite à l'annuaire
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </>
      )}
    </section>
  );
};

// Sections libres d'une fiche matériau (ex. « Terre cuite, grès, faïence… »),
// injectées après le processus ou après « Pourquoi » selon la clé utilisée.
const ExtraSections = ({ sections }) =>
  (sections || []).map((s) => (
    <div key={s.h2}>
      <H2>{s.h2}</H2>
      {(s.paras || []).map((p, i) => (
        <P key={i}>{p}</P>
      ))}
      {s.list && (
        <ul className="mt-6 divide-y divide-ink/10 border-y border-ink/10">
          {s.list.map(([titre, texte]) => (
            <li key={titre} className="grid gap-1 py-4 sm:grid-cols-12 sm:gap-6">
              <span className="font-serif text-lg text-ink sm:col-span-3">{titre}</span>
              <span className="text-sm font-light leading-relaxed text-smoke sm:col-span-9">
                <RichText text={texte} />
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  ));

export default function MaterialDetailPage() {
  const { slug } = useParams();
  const m = MATERIAUX_DETAIL[slug];

  useSEO(
    m ? m.seoTitle : "Matériau — Sculpteur Français",
    m ? m.seoDescription : "Fiche matériau de la sculpture contemporaine.",
    m
      ? {
          "@context": "https://schema.org",
          "@type": "Article",
          headline: `${m.name} en sculpture contemporaine`,
          description: m.seoDescription,
          inLanguage: "fr-FR",
          mainEntityOfPage: `${BASE}/materiaux-et-techniques/${slug}`,
          author: { "@type": "Organization", name: "Sculpteur Français", url: `${BASE}` },
          publisher: { "@type": "Organization", name: "Sculpteur Français", url: `${BASE}` },
        }
      : null,
    { type: "article" }
  );

  if (!m) return <Navigate to="/materiaux-et-techniques" replace />;

  return (
    <ArticleShell
      testId={`materiau-${slug}-page`}
      eyebrow={`Fiche matériau — ${m.badge}`}
      titleLines={m.h1Lines || [m.name, "en sculpture contemporaine"]}
      lead={m.lead}
      image={{
        ...m.image,
        to: {
          bronze: "/lexique-sculpture/fonte",
          resine: "/lexique-sculpture/resine",
          platre: "/lexique-sculpture/taille-directe",
          metal: "/lexique-sculpture/assemblage",
          bois: "/lexique-sculpture/taille-directe",
          pierre: "/lexique-sculpture/taille-directe",
          terre: "/lexique-sculpture/terre-cuite",
        }[slug],
      }}
    >
      <Link
        to="/materiaux-et-techniques"
        data-testid="materiau-back-link"
        className="group mb-2 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em] text-bronze"
      >
        <ArrowLeft className="h-3.5 w-3.5 transition-transform duration-300 group-hover:-translate-x-1" />
        <span className="link-underline">Tous les matériaux</span>
      </Link>

      <div className="grid gap-px border border-ink/15 bg-ink/10 sm:grid-cols-2 lg:grid-cols-4" data-testid="materiau-facts">
        {m.facts.map(([label, value]) => (
          <div key={label} className="bg-paper p-5">
            <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-bronze">{label}</p>
            <p className="mt-2 font-serif text-lg leading-snug text-ink">{value}</p>
          </div>
        ))}
      </div>

      <H2>{m.processTitle || "Comment le travaille-t-on ?"}</H2>
      <P>
        Du modèle à la finition, voici les grandes étapes qui donnent corps à
        une œuvre dans ce matériau.
      </P>
      <ol className="mt-8 divide-y divide-ink/10 border-y border-ink/10" data-testid="materiau-process">
        {m.process.map(([title, text], i) => (
          <li key={title} className="grid gap-2 py-5 sm:grid-cols-12 sm:gap-6">
            <span className="font-mono text-sm text-bronze sm:col-span-2">
              Étape {String(i + 1).padStart(2, "0")}
            </span>
            <span className="font-serif text-xl text-ink sm:col-span-4">{title}</span>
            <span className="text-sm font-light leading-relaxed text-smoke sm:col-span-6"><RichText text={text} /></span>
            {MATERIAU_STEP_ILLUS[slug]?.[i] && (
              <div className="sm:col-span-6 sm:col-start-7">
                <IlluFigure
                  entry={MATERIAU_STEP_ILLUS[slug][i].main}
                  testId={`materiau-illu-${i}`}
                  className="mt-2"
                />
                {MATERIAU_STEP_ILLUS[slug][i].extra && (
                  <IlluFigure
                    entry={MATERIAU_STEP_ILLUS[slug][i].extra}
                    testId={`materiau-illu-${i}-outil`}
                    className="mt-6"
                  />
                )}
              </div>
            )}
          </li>
        ))}
      </ol>

      <ExtraSections sections={m.apresProcess} />

      <H2>Le rendu</H2>
      {m.rendu.map((p, i) => (
        <P key={i}>{p}</P>
      ))}

      <H2>{m.pourquoiTitle || "Pourquoi les sculpteurs le choisissent"}</H2>
      <ul className="mt-6 grid gap-px border border-ink/15 bg-ink/10 sm:grid-cols-2" data-testid="materiau-pourquoi">
        {m.pourquoi.map((point) => (
          <li key={point} className="flex items-start gap-3 bg-paper p-5">
            <Check className="mt-0.5 h-4 w-4 shrink-0 text-bronze" />
            <span className="text-sm font-light leading-relaxed text-body">{point}</span>
          </li>
        ))}
      </ul>

      <ExtraSections sections={m.apresPourquoi} />

      <H2>Entretien et conservation</H2>
      <P><RichText text={m.entretien} /></P>

      <SeeAlso
        links={
          m.seeAlso || [
            { to: "/materiaux-et-techniques", label: "Vue d'ensemble des matériaux" },
            { to: "/lexique-sculpture", label: "Le lexique : patine, cire perdue, tirage…" },
            ...OTHERS(slug).slice(0, 1),
          ]
        }
      />

      <PartnersSection slug={slug} />
    </ArticleShell>
  );
}
